import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { MutationStateFns } from '@/api/utils/mutation';
import { useAuth } from '@/context/AuthProvider';

import { QUERY_KEY } from './useGetGroupNegotiations';

export type ApiRequest_Close_Group_Negotiation = {
  communityId: number;
  negotiationId: number;
};

export type ApiResponse_Close_Group_Negotiation = {
  id: number;
  community_id: number;
  initiator_id: number;
  title: string;
  description?: string;
  status: string;
  created_at: string;
  updated_at: string;
  closed_at: string;
  end_date?: string;
  metadata?: Record<string, unknown>;
};

const METHOD = 'POST';
const ENDPOINT = 'communities/{communityId}/group-negotiations/{negotiationId}/close';

export const useCloseGroupNegotiation = (
  stateFns?: MutationStateFns<ApiResponse_Close_Group_Negotiation>
) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ApiRequest_Close_Group_Negotiation) =>
      handleApiCall<ApiResponse_Close_Group_Negotiation, Record<string, never>>(
        session,
        METHOD,
        getResolvedEndpoint(ENDPOINT, {
          communityId: params.communityId.toString(),
          negotiationId: params.negotiationId.toString(),
        }),
        {}
      ),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY({ communityId: variables.communityId }),
      });
      enqueueSnackbar('Save together group closed successfully!', { variant: 'success' });
      stateFns?.onSuccess?.(data);
    },
    onError: error => {
      enqueueSnackbar('Failed to close save together group.', { variant: 'error' });
      stateFns?.onError?.(error as AxiosError);
    },
  });
};